/**
 * Authenticated download for /uploads/documents, /uploads/chat-exports, and
 * /uploads/chat. Clients fetch with Bearer and use blob URLs for display.
 */

export function sameOriginUploadUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  try {
    const base = typeof window !== 'undefined' && window.location ? window.location.origin : undefined;
    const parsed = new URL(raw, base);
    if (parsed.pathname.indexOf('/uploads/') === 0) return parsed.pathname + parsed.search;
    if (base && parsed.origin === base) return parsed.pathname + parsed.search;
    return parsed.href;
  } catch {
    return raw.startsWith('/') ? raw : raw;
  }
}

export function needsAuthedUpload(url) {
  const href = sameOriginUploadUrl(url);
  return href.startsWith('/uploads/documents/')
    || href.startsWith('/uploads/chat-exports/')
    || href.startsWith('/uploads/chat/');
}

function accessToken() {
  try {
    return localStorage.getItem('accessToken') || '';
  } catch {
    return '';
  }
}

/** Fetch a protected upload and return an object URL (caller may revoke). */
export async function fetchProtectedUploadObjectUrl(url, opts = {}) {
  const href = sameOriginUploadUrl(url);
  if (!needsAuthedUpload(href)) throw new Error('Not a protected upload path');
  const pathOnly = href.split('?')[0];
  const q = new URLSearchParams({ path: pathOnly });
  if (opts.inline) q.set('inline', '1');
  const token = accessToken();
  const resp = await fetch(`/api/v1/files?${q.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!resp.ok) {
    let msg = 'Download failed';
    try {
      const body = await resp.json();
      if (body && body.error) msg = body.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  const blob = await resp.blob();
  return URL.createObjectURL(blob);
}

export async function downloadProtectedUpload(url, opts = {}) {
  const href = sameOriginUploadUrl(url);
  if (!needsAuthedUpload(href)) throw new Error('Not a protected upload path');
  const pathOnly = href.split('?')[0];
  const objectUrl = await fetchProtectedUploadObjectUrl(href, opts);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = pathOnly.split('/').pop() || 'download';
  a.rel = 'noopener';
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

/**
 * Fill img/video nodes that have data-protected-src with Bearer → blob URLs.
 * Safe to call repeatedly; skips nodes that already have a blob src.
 */
export function hydrateProtectedMedia(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  const nodes = root.querySelectorAll('img[data-protected-src], video[data-protected-src]');
  nodes.forEach((node) => {
    if (node.dataset.protectedHydrated === '1') return;
    const src = node.getAttribute('data-protected-src') || '';
    if (!src || !needsAuthedUpload(src)) return;
    node.dataset.protectedHydrated = '1';
    fetchProtectedUploadObjectUrl(src).then((objectUrl) => {
      node.src = objectUrl;
      const parent = node.closest && node.closest('a[data-protected-href]');
      if (parent) parent.href = objectUrl;
    }).catch((err) => {
      console.warn('[upload]', err && err.message);
      node.dataset.protectedHydrated = '0';
    });
  });
}

export function bindProtectedUploadClicks(root) {
  if (!root || typeof root.addEventListener !== 'function') return;
  if (root.dataset && root.dataset.bonnieUploadBound === '1') return;
  if (root.dataset) root.dataset.bonnieUploadBound = '1';
  root.addEventListener('click', (ev) => {
    const a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
    if (!a) return;
    // Media already hydrated to a blob: let the browser open/download it.
    if (a.href && String(a.href).indexOf('blob:') === 0) return;
    const href = a.getAttribute('data-protected-href') || a.getAttribute('href') || '';
    if (!needsAuthedUpload(href)) return;
    ev.preventDefault();
    ev.stopPropagation();
    downloadProtectedUpload(href).catch((err) => {
      console.warn('[download]', err && err.message);
    });
  });
}
